import axios from 'axios';
import { authService } from './authService';

// Add debug logging function
const logDebug = (message, data) => {
    // Only log in development or when debugging is enabled
    if (process.env.NODE_ENV === 'development' || process.env.REACT_APP_ENABLE_DEBUGGER === 'true') {
        // console.log(`[API Debug] ${message}`, data);
        // You can also use alert for more visible debugging in Capacitor
        if (window.Capacitor?.isNativePlatform()) {
           // console.log(`[API Debug] ${message}`, JSON.stringify(data));
        }
    }
};

// Check if running in Capacitor
const isCapacitor = window.Capacitor?.isNativePlatform() || false;

// Use proxy only in Capacitor and when not in production, or when explicitly enabled
const useProxy = false; // Proxy is disabled in production

// Get the API URL from environment variables
let apiBaseUrl = process.env.REACT_APP_API_URL;
if (!apiBaseUrl) {
    throw new Error('REACT_APP_API_URL environment variable is not set');
}

// Ensure API URL doesn't have a trailing slash (axios will handle it)
apiBaseUrl = apiBaseUrl.endsWith('/') ? apiBaseUrl.slice(0, -1) : apiBaseUrl;

// Construct proxy URL from API base URL
const proxyUrl = `${apiBaseUrl}/proxy.php?url=`;

// Log the API URL for debugging
logDebug('API URL', apiBaseUrl);
logDebug('Using Proxy', useProxy);

// Helper to ensure URL doesn't have double slashes
const normalizeUrl = (url) => {
    // Remove trailing slash from base URL if present
    const baseWithoutTrailingSlash = apiBaseUrl.endsWith('/') 
        ? apiBaseUrl.slice(0, -1) 
        : apiBaseUrl;
    
    // Add leading slash to endpoint if not present
    const endpointWithLeadingSlash = url.startsWith('/') 
        ? url 
        : '/' + url;
    
    return baseWithoutTrailingSlash + endpointWithLeadingSlash;
};

const getFullUrl = (endpoint) => {
    if (!useProxy) {
        // For direct calls, just normalize the URL
        return endpoint.startsWith('http') ? endpoint : normalizeUrl(endpoint);
    }
    
    // If endpoint is already a full URL, encode it directly
    if (endpoint.startsWith('http')) {
        return `${proxyUrl}${encodeURIComponent(endpoint)}`;
    }
    
    // Otherwise, combine with base URL then encode
    const fullUrl = normalizeUrl(endpoint);
    return `${proxyUrl}${encodeURIComponent(fullUrl)}`;
};

const api = axios.create({
    baseURL: useProxy ? '' : apiBaseUrl, // Don't use baseURL with proxy
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    },
    // Timeout set to prevent ANR (Application Not Responding) issues
    // 15 seconds is sufficient for most API calls and prevents UI freezing
    timeout: 15000, // 15 seconds - prevents ANR on slow networks
    // iOS-specific: Ensure proper request handling
    validateStatus: function (status) {
        return status >= 200 && status < 300; // default
    },
    // Don't send credentials with requests (we use JWT tokens in headers instead)
    withCredentials: false
});

// Request interceptor
api.interceptors.request.use(
    (config) => {
        // Validate API URL is set (critical for iOS builds)
        if (!apiBaseUrl) {
            const error = new Error('REACT_APP_API_URL is not configured. Please check your build environment variables.');
            console.error('[API Configuration Error]', error.message);
            return Promise.reject(error);
        }
        
        const token = authService.getAccessToken();
        logDebug('Request interceptor - token exists:', !!token);
        
        // Handle URL for proxy if needed
        if (useProxy && config.url && !config.url.includes(proxyUrl)) {
            // If using proxy and URL doesn't already include proxy
            config.url = getFullUrl(config.url);
        }
        // Note: If not using proxy, axios will automatically prepend baseURL to relative URLs
        // So we don't need to manually construct the full URL here
        
        // Log the full request URL for debugging (especially important for iOS)
        const fullUrl = config.url.startsWith('http') 
            ? config.url 
            : `${config.baseURL || apiBaseUrl}${config.url.startsWith('/') ? '' : '/'}${config.url}`;
        console.log(`[API Request] ${config.method?.toUpperCase()} ${fullUrl}`);
        console.log(`[API Config] baseURL: ${config.baseURL || apiBaseUrl}, url: ${config.url}`);
        logDebug('Request URL:', config.url);
        logDebug('Base URL:', config.baseURL);
        logDebug('API Base URL:', apiBaseUrl);
        
        if (token) {
            // Ensure headers object exists
            config.headers = config.headers || {};
            
            // Set Authorization header
            config.headers['Authorization'] = `Bearer ${token}`;
            
            logDebug('Request config:', {
                url: config.url,
                method: config.method,
                headers: config.headers,
                data: config.data
            });
        } else {
            logDebug('No token available for request', config.url);
        }
        
        return config;
    },
    (error) => {
        console.error('[API Request Error]', error);
        return Promise.reject(error);
    }
);

// Response interceptor
api.interceptors.response.use(
    (response) => {
        logDebug('Response success:', {
            status: response.status,
            url: response.config.url,
            data: response.data
        });
        return response;
    },
    async (error) => {
        // Enhanced error logging for timeout issues
        if (error.code === 'ECONNABORTED') {
            // Timeout occurred - log for debugging
            console.warn('[API Timeout] Request timed out after 15 seconds:', {
                url: error.config?.url,
                method: error.config?.method
            });
            
            // For Android, timeouts can cause ANR if not handled properly
            // The timeout is set to 15 seconds to prevent ANR (Android kills apps after ~5 seconds of blocking)
            if (window.Capacitor?.isNativePlatform()) {
                console.warn('[API Timeout] This timeout prevents ANR on Android');
            }
        }
        
        // Enhanced error logging for iOS network issues
        const errorInfo = {
            status: error.response?.status,
            url: error.config?.url,
            baseURL: error.config?.baseURL,
            fullUrl: error.config?.url?.startsWith('http') 
                ? error.config.url 
                : `${error.config?.baseURL || apiBaseUrl}${error.config?.url}`,
            data: error.response?.data,
            message: error.message,
            code: error.code,
            apiBaseUrl: apiBaseUrl,
            isCapacitor: window.Capacitor?.isNativePlatform() || false
        };
        
        console.error('[API Response Error]', errorInfo);
        logDebug('Response error:', errorInfo);
        
        const originalRequest = error.config;

        // If error is 401 (Unauthorized) and we haven't tried to refresh token yet
        if (error.response?.status === 401 && !originalRequest._retry) {
            logDebug('Token expired, attempting refresh...', {});
            originalRequest._retry = true;

            try {
                const refreshToken = authService.getRefreshToken();
                if (!refreshToken) {
                    throw new Error('No refresh token available');
                }

                logDebug('Using refresh token', { refreshToken: refreshToken.substring(0, 10) + '...' });

                // Try to get new tokens using refresh token
                let refreshUrl = normalizeUrl('refresh_token.php');
                logDebug('Refresh URL:', refreshUrl);
                
                if (useProxy) {
                    refreshUrl = `${proxyUrl}${encodeURIComponent(refreshUrl)}`;
                }
                
                const response = await axios.post(refreshUrl, 
                    { refresh_token: refreshToken },
                    {
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    }
                );

                logDebug('Refresh token response:', response.data);

                if (response.data.success) {
                    logDebug('Token refresh successful', {});
                    // Save new tokens
                    authService.setTokens(
                        response.data.access_token,
                        response.data.refresh_token
                    );

                    // Update the failed request with new token and retry
                    originalRequest.headers.Authorization = `Bearer ${response.data.access_token}`;
                    return api(originalRequest);
                } else {
                    throw new Error(`Token refresh failed: ${response.data.message || 'Unknown error'}`);
                }
            } catch (error) {
                logDebug('Token refresh failed:', {
                    message: error.message,
                    response: error.response?.data,
                    status: error.response?.status
                });
                // Clear tokens and redirect to login
                authService.clearTokens();
                // Add a small delay to ensure logs are visible before redirect
                setTimeout(() => {
                    window.location.href = '/';
                }, 500);
                return Promise.reject(error);
            }
        }

        return Promise.reject(error);
    }
);

// Original post method
const originalPost = api.post;

// Override post method to handle form data properly with proxy
api.post = function(url, data, config = {}) {
    // Ensure config and headers exist
    config = config || {};
    config.headers = config.headers || {};
    
    // If data is FormData, remove the Content-Type header to let the browser set it
    if (data instanceof FormData) {
        config.headers['Content-Type'] = undefined; // Let browser set the correct boundary for multipart/form-data
        logDebug('FormData detected, setting Content-Type to undefined', {});
    }
    // If data is URLSearchParams, ensure proper Content-Type for browser and iOS
    else if (data instanceof URLSearchParams) {
        // Convert URLSearchParams to string for better compatibility
        const formDataString = data.toString();
        // Ensure Content-Type is properly set and not overridden
        config.headers = {
            ...config.headers,
            'Content-Type': 'application/x-www-form-urlencoded'
        };
        logDebug('URLSearchParams detected, converting to string', {
            url: url,
            dataLength: formDataString.length,
            contentType: config.headers['Content-Type']
        });
        // Use the string version instead
        return originalPost.call(this, url, formDataString, config);
    }
    
    // If using proxy and data is a string (form data), we need special handling
    if (useProxy && typeof data === 'string' && data.includes('=')) {
        // For form data, we need to ensure it's properly handled with the proxy
        config.headers['Content-Type'] = 'application/x-www-form-urlencoded';
        return originalPost.call(this, url, data, config);
    }
    
    // Otherwise use the original post method
    return originalPost.call(this, url, data, config);
};

// Modify the existing api object to include file upload handling
api.uploadFile = async (url, data, config = {}) => {
    try {
        const token = authService.getAccessToken();
        
        // Handle URL for proxy if needed
        const uploadUrl = useProxy ? getFullUrl(url) : url;
        logDebug('Uploading file to:', uploadUrl);
        
        // Remove Content-Type header for file uploads and add Authorization
        const uploadConfig = {
            ...config,
            headers: {
                ...config.headers,
                'Content-Type': undefined, // Let browser set the correct boundary for multipart/form-data
                'Authorization': token ? `Bearer ${token}` : undefined
            }
        };

        logDebug('Upload config:', uploadConfig);

        const response = await axios.post(uploadUrl, data, {
            ...uploadConfig,
            withCredentials: true
        });
        
        logDebug('Upload response:', response.data);
        return response;
    } catch (error) {
        logDebug('API Upload Error:', {
            message: error.message,
            response: error.response?.data
        });
        throw error;
    }
};

export default api; 